import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Download, Eye, Lock, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authenticating, setAuthenticating] = useState(false);
  
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 20;
  
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Check for stored password on mount
  useEffect(() => {
    const storedPassword = sessionStorage.getItem('admin_password');
    if (storedPassword) {
      setPassword(storedPassword);
      setAuthenticated(true);
    }
  }, []);

  // Fetch enquiries when authenticated or page changes
  useEffect(() => {
    if (authenticated && password) {
      fetchEnquiries();
    }
  }, [authenticated, page]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthenticating(true);
    setAuthError(null);
    
    try {
      await axios.post(`${API}/admin/auth`, { password });
      sessionStorage.setItem('admin_password', password);
      setAuthenticated(true);
    } catch (err) {
      setAuthError('Invalid password');
    } finally {
      setAuthenticating(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    setAuthenticated(false);
    setPassword('');
    setEnquiries([]);
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/admin/enquiries`, {
        params: {
          password,
          skip: page * limit,
          limit
        }
      });
      setEnquiries(response.data.enquiries);
      setTotal(response.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setAuthError('Session expired. Please log in again.');
      } else {
        setError('Failed to fetch enquiries');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setDialogOpen(true);
  };

  const exportCSV = () => {
    window.open(`${API}/admin/enquiries/export/csv?password=${encodeURIComponent(password)}`, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="bg-white border border-stone-200 rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-5 h-5 text-stone-500" />
              <h1 className="text-xl font-semibold text-stone-900">Admin Login</h1>
            </div>
            
            {authError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{authError}</p>
              </div>
            )}
            
            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-stone-700 font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-white border-stone-300"
                  placeholder="Enter admin password"
                  required
                  disabled={authenticating}
                />
              </div>
              <Button
                type="submit"
                disabled={authenticating}
                className="w-full bg-stone-800 hover:bg-stone-900 text-white"
              >
                {authenticating ? 'Verifying...' : 'Log in'}
              </Button>
            </form>
          </div>
          
          <p className="mt-4 text-center text-sm text-stone-500">
            Site & Signal Admin
          </p>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-stone-900">Enquiries</h1>
            <p className="text-sm text-stone-500">{total} total</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={exportCSV}
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-stone-600 hover:text-stone-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
            <p className="text-stone-500">Loading enquiries...</p>
          </div>
        ) : enquiries.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
            <p className="text-stone-500">No enquiries yet</p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="font-semibold text-stone-700">Date</TableHead>
                    <TableHead className="font-semibold text-stone-700">Name</TableHead>
                    <TableHead className="font-semibold text-stone-700">Email</TableHead>
                    <TableHead className="font-semibold text-stone-700">Phone</TableHead>
                    <TableHead className="font-semibold text-stone-700">Business</TableHead>
                    <TableHead className="font-semibold text-stone-700 w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((enquiry) => (
                    <TableRow key={enquiry.id} className="hover:bg-stone-50">
                      <TableCell className="text-stone-600 text-sm">
                        {formatDate(enquiry.createdAt)}
                      </TableCell>
                      <TableCell className="font-medium text-stone-900">
                        {enquiry.name}
                      </TableCell>
                      <TableCell className="text-stone-600">
                        <a href={`mailto:${enquiry.email}`} className="hover:text-stone-900">
                          {enquiry.email}
                        </a>
                      </TableCell>
                      <TableCell className="text-stone-600">
                        {enquiry.phone || '-'}
                      </TableCell>
                      <TableCell className="text-stone-600">
                        {enquiry.businessType || '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => viewEnquiry(enquiry)}
                          variant="ghost"
                          size="sm"
                          className="text-stone-500 hover:text-stone-900"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-stone-500">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    variant="outline"
                    size="sm"
                    className="border-stone-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    variant="outline"
                    size="sm"
                    className="border-stone-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Enquiry detail dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-stone-500">Name</p>
                  <p className="font-medium text-stone-900">{selectedEnquiry.name}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Date</p>
                  <p className="font-medium text-stone-900">{formatDate(selectedEnquiry.createdAt)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-stone-500">Email</p>
                <a href={`mailto:${selectedEnquiry.email}`} className="font-medium text-stone-900 hover:underline">
                  {selectedEnquiry.email}
                </a>
              </div>
              
              <div>
                <p className="text-sm text-stone-500">Phone</p>
                <p className="font-medium text-stone-900">{selectedEnquiry.phone || '-'}</p>
              </div>
              
              <div>
                <p className="text-sm text-stone-500">Business Type</p>
                <p className="font-medium text-stone-900">{selectedEnquiry.businessType || '-'}</p>
              </div>
              
              <div>
                <p className="text-sm text-stone-500">Message</p>
                <p className="font-medium text-stone-900 whitespace-pre-wrap">
                  {selectedEnquiry.message || '-'}
                </p>
              </div>
              
              <div className="pt-4 border-t border-stone-200">
                <p className="text-xs text-stone-400">Source: {selectedEnquiry.sourcePageUrl || '-'}</p>
                <p className="text-xs text-stone-400">IP Hash: {selectedEnquiry.ipHash || '-'}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
